/**
 * Top-level Floating Actions orchestrator.
 *
 * Layout:
 *   - GPS FAB stays on the left side and remains autonomous (kept
 *     outside the grouped burst so the "no-fix" blinking stays
 *     visible to users waiting for their first GPS fix).
 *   - The main grouped burst sits on the right side and wraps the
 *     Itinerary settings / Map style / Pricing / Navigate sub-FABs.
 *   - The Dynamic Island (when active) sits inline between the two
 *     primary FABs on the same vertical baseline.
 *   - Both visible FABs share the same horizontal gutters as the
 *     search fields, so they stay horizontally flush with the bars'
 *     left/right edges.
 *
 * Behavior:
 *   - The grouped burst auto-opens once on first launch (via
 *     `useBurstAutoOpenReveal`) to hint at its discoverability.
 *   - Modal sub-FABs leave the burst open when their MiniModal opens;
 *     panel close handlers are registered through the shared
 *     `FloatingActionBurstContext` so the main FAB can dismiss them.
 *   - The menu vertical position follows the bottom edge of the
 *     search fields via the `searchBarsBottomPx` prop, so the FABs
 *     move in sync with the bars when merge mode toggles.
 */

import { motion } from "motion/react";
import React, { useCallback, useState } from "react";
import { DYNAMIC_ISLAND } from "../../config/dynamicIsland.configuration";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { isNavigationEnabled } from "../../services/FrontendConfigApi";
import { fabUiSlice, usePersistedSlice } from "../../store/persistence";
import { type FabExpansionMode } from "../../types/GlobalContext";
import { type MapAppearanceMode, type MapStyleId, type MapStyleOverride } from "../../types/MapStyle";
import { DynamicIslandSlot, useDynamicIslandFabGapPercent, type DynamicIslandSlotProps } from "../DynamicIsland";
import FloatingActionBurst from "./FloatingActionBurst";
import {
    DEFAULT_SAFE_MARGIN_BOTTOM_PX,
    FALLBACK_TOP_PX,
    FLOATING_MENU_FAB_ROW_ELEMENT_ID,
    FLOATING_MENU_LAYER_ELEMENT_ID,
    ITINERARY_PANEL_VERTICAL_GAP_PX,
    SEARCH_BARS_VERTICAL_GAP_PX,
} from "./FloatingActionBurst/floatingActionBurst.configuration";
import {
    LoginSubFloatingAction,
    MapStyleSubFloatingAction,
    NavigateSubFloatingAction,
    PricingPrefsSubFloatingAction,
    SavedItinerariesSubFloatingAction,
    TransportsPrefsSubFloatingAction,
} from "./FloatingActionBurst/SubActions";

import { useBurstAutoOpenReveal } from "./FloatingActionBurst/useBurstAutoOpenReveal.hook";
import FloatingActionGps from "./FloatingActionGps";

/** Public props accepted by the floating menu orchestrator. */
export interface FloatingMenuProps {

    /** Enables dark palette variants for every child FAB. */
    "isDarkUi": boolean;

    /** Current map appearance mode (forwarded to the Map Style sub-FAB). */
    "appearanceMode": MapAppearanceMode;

    /** Current map style override (forwarded to the Map Style sub-FAB). */
    "styleOverride": MapStyleOverride;

    /** Current default style id (forwarded to the Map Style sub-FAB). */
    "defaultStyleId": MapStyleId;

    /** Fired when the user picks a new map appearance mode. */
    "onAppearanceModeChange": ( mode: MapAppearanceMode ) => void;

    /** Fired when the user picks a new map style override. */
    "onStyleOverrideChange": ( styleOverride: MapStyleOverride ) => void;

    /** Notified when the transports prefs MiniModal open state changes. */
    "onTransportsPrefsPanelOpenStateChange": ( isOpen: boolean ) => void;

    /** Notified when the pricing prefs MiniModal open state changes. */
    "onPricingPrefsPanelOpenStateChange"?: ( isOpen: boolean ) => void;

    /** Fired when the user clicks the GPS FAB. */
    "onReturnToUserLocation": () => void;

    /** Fired when the user clicks the Navigate sub-FAB. */
    "onNavigate": () => void;

    /** Opens the saved-itinerary list in the itinerary panel. */
    "onOpenSavedItineraries": () => void;

    /** Count of saved itineraries with upcoming departures (sub-FAB badge). */
    "futureSavedItineraryCount": number;

    /** Whether a search autocomplete dropdown is currently visible. */
    "isSearchAutocompleteOpen": boolean;

    /** Current pixel height of the bottom itinerary panel. */
    "itineraryPanelHeight": number;

    /**
     * Absolute Y coordinate (px from the viewport top) of the lowest
     * edge of the search fields. Drives the floating menu's dynamic
     * `top` so the FABs follow the bars when merge mode toggles or
     * when the autocomplete dropdown expands. `null` falls back to
     * `FALLBACK_TOP_PX` to prevent visual jumps on initial paint.
     */
    "searchBarsBottomPx": number | null;

    /** Props for the Dynamic Island slot (undefined when module disabled). */
    "dynamicIslandSlotProps"?: DynamicIslandSlotProps;
}

/**
 * FloatingMenu — renders and wires every on-map floating action.
 *
 * @param props Component props. See `FloatingMenuProps`.
 * @returns The rendered floating menu layer.
 */
const FloatingMenu: React.FC<FloatingMenuProps> = ( {
    isDarkUi,
    appearanceMode,
    styleOverride,
    defaultStyleId,
    onAppearanceModeChange,
    onStyleOverrideChange,
    onTransportsPrefsPanelOpenStateChange,
    onPricingPrefsPanelOpenStateChange,
    onReturnToUserLocation,
    onNavigate,
    onOpenSavedItineraries,
    futureSavedItineraryCount,
    isSearchAutocompleteOpen,
    itineraryPanelHeight,
    searchBarsBottomPx,
    dynamicIslandSlotProps,
} ) => {
    const [ fabUi, setFabUi ] = usePersistedSlice( fabUiSlice );
    const [ isBurstOpen, setIsBurstOpen ] = useState<boolean>( false );
    const [ hasDynamicIslandLayoutOccupied, setHasDynamicIslandLayoutOccupied ] = useState<boolean>( dynamicIslandSlotProps?.payload !== undefined );
    const reducedMotion = useReducedMotion();

    /**
     * Toggle the persisted expansion mode between `arc` and `line`.
     * Triggered by a long-press on the main FAB (no-op at runtime while
     * `FAB_BURST_LOCK_VERTICAL_DOWN` is enabled in burst configuration).
     */
    const handleFabModeToggle = useCallback( (): void => {
        const nextMode: FabExpansionMode = fabUi.fabExpansionMode === "arc" ? "line" : "arc";
        setFabUi( ( previous ) => ( {
            ...previous,
            "fabExpansionMode": nextMode,
        } ) );
    }, [ fabUi.fabExpansionMode, setFabUi ] );

    // One-shot reveal on the user's first launch of the app.
    useBurstAutoOpenReveal( { "setOpen": setIsBurstOpen } );

    const dynamicIslandFabGapPercent = useDynamicIslandFabGapPercent();

    const fabRowLayoutTransition = reducedMotion || !DYNAMIC_ISLAND.animations.enabled
        ? { "duration": 0 }
        : DYNAMIC_ISLAND.animations.spring;

    /*
     * Bottom safe area: either the measured ItineraryPanel height (plus a
     * small breathing gap), or a sensible default when the panel
     * height has not been reported yet.
     */
    const safeMarginBottomPx = itineraryPanelHeight > 0
        ? itineraryPanelHeight + ITINERARY_PANEL_VERTICAL_GAP_PX
        : DEFAULT_SAFE_MARGIN_BOTTOM_PX;

    /*
     * Vertical offset anchored on the measured bottom edge of the
     * search fields plus a small breathing gap. Home re-samples that edge
     * on every animation frame while the arrival field's `top` CSS
     * transition runs, so no separate `top` transition is applied here
     * (avoids lagging one full transition behind the search chrome).
     */
    const topOffsetPx = searchBarsBottomPx !== null
        ? searchBarsBottomPx + SEARCH_BARS_VERTICAL_GAP_PX
        : FALLBACK_TOP_PX;

    const safeMarginTopPx = topOffsetPx;

    return (
        <div
            className="flex flex-col z-40 w-full relative pointer-events-none"
            id={FLOATING_MENU_LAYER_ELEMENT_ID}
            style={{ "top": `${ topOffsetPx }px` }}
        >
            {/*
              * Main FAB row. The inner container mirrors the search
              * fields' outer width constraints so the GPS and grouped
              * burst are horizontally flush with the left / right
              * edges of the search bars. The Dynamic Island sits inline
              * between the two primary FABs when a payload is active:
              *   - Mobile: `w-full` + `px-3` (matches `left-3 right-3`).
              *   - Desktop (`md:`): `w-3/5 mx-auto` (matches `md:w-3/5`).
              */}
                <div
                    className={`relative flex w-full md:w-3/5 mx-auto items-center px-3 md:px-0 pointer-events-none ${
                            hasDynamicIslandLayoutOccupied ? "" : "justify-between"
                        }`}
                    id={FLOATING_MENU_FAB_ROW_ELEMENT_ID}
                    style={hasDynamicIslandLayoutOccupied
                            ? { "columnGap": `${ dynamicIslandFabGapPercent }%` }
                            : undefined}
                >
                    <motion.div
                        className="pointer-events-auto shrink-0"
                        layout
                        transition={fabRowLayoutTransition}
                    >
                        <FloatingActionGps
                            isDarkUi={isDarkUi}
                            isSearchAutocompleteOpen={isSearchAutocompleteOpen}
                            returnToUserLocation={onReturnToUserLocation}
                        />
                    </motion.div>
                    {dynamicIslandSlotProps !== undefined && (
                        <motion.div
                            className={
                                hasDynamicIslandLayoutOccupied
                                    ? "flex-1 min-w-0 flex items-center justify-center"
                                    : "w-0 min-w-0 overflow-visible"
                            }
                            layout
                            transition={fabRowLayoutTransition}
                        >
                            <DynamicIslandSlot
                                {...dynamicIslandSlotProps}
                                onLayoutOccupancyChange={setHasDynamicIslandLayoutOccupied}
                            />
                        </motion.div>
                    )}
                    <motion.div
                        className="pointer-events-auto shrink-0"
                        layout
                        transition={fabRowLayoutTransition}
                    >
                        <FloatingActionBurst
                            ariaLabel="Menu de réglages"
                            isDarkUi={isDarkUi}
                            isOpen={isBurstOpen}
                            isSearchAutocompleteOpen={isSearchAutocompleteOpen}
                            mode={fabUi.fabExpansionMode}
                            onModeToggle={handleFabModeToggle}
                            onOpenChange={setIsBurstOpen}
                            safeMarginBottomPx={safeMarginBottomPx}
                            safeMarginTopPx={safeMarginTopPx}
                        >
                        {/*
                          * Child order maps to arc / line progression (index 0 is
                          * closest to the main FAB along the computed path):
                          * saved itineraries → itinerary prefs → map → pricing →
                          * login/account → navigate (optional).
                          */}
                        <SavedItinerariesSubFloatingAction
                            futureSavedCount={futureSavedItineraryCount}
                            isDarkUi={isDarkUi}
                            onOpenSavedItineraries={onOpenSavedItineraries}
                        />
                        <TransportsPrefsSubFloatingAction
                            isDarkUi={isDarkUi}
                            onOpenStateChange={onTransportsPrefsPanelOpenStateChange}
                        />
                        <MapStyleSubFloatingAction
                            appearanceMode={appearanceMode}
                            defaultStyleId={defaultStyleId}
                            isDarkUi={isDarkUi}
                            onAppearanceModeChange={onAppearanceModeChange}
                            onStyleOverrideChange={onStyleOverrideChange}
                            styleOverride={styleOverride}
                        />
                        <PricingPrefsSubFloatingAction
                            isDarkUi={isDarkUi}
                            onOpenStateChange={onPricingPrefsPanelOpenStateChange}
                        />
                        <LoginSubFloatingAction isDarkUi={isDarkUi} />
                        {isNavigationEnabled() && (
                            <NavigateSubFloatingAction
                                isDarkUi={isDarkUi}
                                onNavigate={onNavigate}
                            />
                        )}
                        </FloatingActionBurst>
                    </motion.div>
                </div>
        </div>
    );
};

export default FloatingMenu;
